import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, getAuthHeader } from '../config/config'; // Assuming you have these

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('Error parsing stored user:', error);
            return null;
        }
    });

    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return localStorage.getItem('isAuthenticated') === 'true';
    });
    const [allowedRoutes, setAllowedRoutes] = useState([]);
    // screenPermissions: map of screenUrl → { canView, canAdd, canEdit, canDelete }
    // Used for granular permission enforcement (e.g. AR Caller module).
    // allowedRoutes is kept unchanged so no other module is affected.
    const [screenPermissions, setScreenPermissions] = useState({});
    const [roleName, setRoleName] = useState(null); // Add new state for roleName
    const [loadingAuth, setLoadingAuth] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const login = async (email, password) => {

        try {

            const response = await axios.post(`${API_BASE_URL}/Login/LoginUser`, { email, password });

            const userData = response.data;

            if (response.data?.token) {
                localStorage.setItem('token', response.data.token);
            }
            if (userData?.refreshToken) {
                localStorage.setItem('refreshToken', userData.refreshToken);
            }
            if (userData?.userId) {
                localStorage.setItem('userId', userData.userId);
            }
            if (userData?.employeeName) {
                localStorage.setItem('userName', userData.employeeName);
            }
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('isAuthenticated', 'true');

            // Set user and authentication state
            setUser(userData);
            setIsAuthenticated(true);

            await fetchPermissions(userData.roleId, userData.userId);

            // Fetch and set role name after successful login
            await fetchRoleName(userData.userId); // Call the new function


            return {
                success: true,
                firstTimeLogin: userData.firstTimeLogin,
                userid: userData.userId
            };
        } catch (error) {
            console.error('Login failed:', error);
            setIsAuthenticated(false);
            setUser(null);
            setRoleName(null); // Clear role name on login failure
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('roleName'); // Clear role name from localStorage
            throw error; // Re-throw to be caught by the login form
        }


    };

    const logout = () => {
        setUser(null);
        setIsAuthenticated(false);
        setAllowedRoutes([]);
        setScreenPermissions({});
        setRoleName(null);
        localStorage.clear();
        localStorage.setItem('logout', Date.now());
        navigate('/');
    };


    const fetchPermissions = async (roleId, userId = null) => {
        try {
            const authToken = await getAuthHeader();
            // GetRoleScreenById already combines role + user privileges when employeeId is provided
            let url = `${API_BASE_URL}/Privilege/GetRoleScreenById?roleId=${roleId}`;
            if (userId) {
                url += `&employeeId=${userId}`;
            }

            console.log('Fetching permissions with URL:', url);
            const response = await axios.get(url, { headers: authToken });
            console.log('Full permissions response:', response.data);

            const data = response?.data || [];
            const allowedUrls = Array.isArray(data)
                ? data
                    .filter(screen => screen && (screen.canView || screen.CanView))
                    .map(screen => screen?.screenUrl || screen?.ScreenUrl)
                    .filter(url => url)
                : [];
            setAllowedRoutes(allowedUrls);

            // Build a screenUrl → privilege flags map for granular enforcement.
            // Only AR Caller currently uses this; all other modules continue to
            // rely solely on allowedRoutes (no behaviour change for them).
            const permMap = {};
            if (Array.isArray(data)) {
                data.forEach(screen => {
                    const url = screen?.screenUrl || screen?.ScreenUrl;
                    if (url) {
                        permMap[url] = {
                            canView:   !!(screen.canView   || screen.CanView),
                            canAdd:    !!(screen.canAdd    || screen.CanAdd),
                            canEdit:   !!(screen.canEdit   || screen.CanEdit),
                            canDelete: !!(screen.canDelete || screen.CanDelete),
                        };
                    }
                });
            }
            setScreenPermissions(permMap);
            console.log('Permissions fetched (Role + User combined):', allowedUrls.length, 'screens with view access');
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setAllowedRoutes([]);
        }
    };

    // New function to fetch role name
    const fetchRoleName = async (employeeId) => {
        try {
            const authToken = await getAuthHeader();
            const response = await axios.get(
                `${API_BASE_URL}/EmployeeDetails/GetRolesByEmployeeId?employeeId=${employeeId}`,
                { headers: authToken }
            );
            const fetchedRoleName = response?.data?.result || null; // Access the 'result' property
            if (fetchedRoleName) {
                setRoleName(fetchedRoleName);
                localStorage.setItem('roleName', fetchedRoleName); // Store role name in localStorage
                console.log('Role Name fetched:', fetchedRoleName);
            }

            return fetchedRoleName;
        } catch (error) {
            console.error('Error fetching role name:', error);
            setRoleName(null);
            localStorage.removeItem('roleName');
            return null;
        }
    };


    useEffect(() => {
        const checkSession = async () => {
            const storedUser = localStorage.getItem('user');
            const storedAuth = localStorage.getItem('isAuthenticated');
            const storedRoleName = localStorage.getItem('roleName');

            if (storedUser && storedAuth === 'true') {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
                setIsAuthenticated(true);
                setRoleName(storedRoleName);
                await fetchPermissions(parsedUser.roleId, parsedUser.userId);
            } else {
                setIsAuthenticated(false);
                setUser(null);
                setRoleName(null);
            }

            setLoadingAuth(false);
        };

        checkSession();
    }, []); // ⚠️ Only run once on mount — don't depend on pathname

    useEffect(() => {
        let logoutTimer;

        const updateActivity = () => {
            localStorage.setItem('lastActivity', Date.now().toString());
        };

        const checkInactivity = () => {
            const last = parseInt(localStorage.getItem('lastActivity') || '0');
            const now = Date.now();
            const diff = now - last;

            if (diff > 30 * 60 * 1000) { // 30 minutes
                logout();
            }
        };

        // Listen to user activity
        const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, updateActivity));

        // Run on mount
        updateActivity();

        logoutTimer = setInterval(checkInactivity, 60 * 1000); // Check every minute

        // Listen for manual logout in other tabs
        const syncLogout = (e) => {
            if (e.key === 'logout') logout();
        };
        window.addEventListener('storage', syncLogout);

        return () => {
            events.forEach(e => window.removeEventListener(e, updateActivity));
            clearInterval(logoutTimer);
            window.removeEventListener('storage', syncLogout);
        };
    }, [isAuthenticated]);



    // Added a check here to ensure loadingAuth is false in the context value
    // only when all initial loading is complete.
    if (loadingAuth) {
        return <div>Loading...</div>;
    }
    return (
        <AuthContext.Provider value={{ user, isAuthenticated, allowedRoutes, screenPermissions, roleName, loadingAuth: false, login, logout, fetchRoleName, fetchPermissions }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};